import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { getPaymentIntentRedirect } from 'src/api/payments';
import type { PaymentIntentRedirectResponseBody } from 'src/api/schema';

export type RedirectIntentData = PaymentIntentRedirectResponseBody['data'];

export type RedirectIntentResponse = PaymentIntentRedirectResponseBody;

function parseRedirectIntentError(err: unknown): {
  status?: number | string;
  message: string;
} {
  if (err instanceof Error) {
    return { message: err.message };
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const o = err as { message?: unknown; status?: number | string };
    return {
      status: o.status,
      message: o.message != null ? String(o.message) : 'request_failed'
    };
  }
  return { message: 'request_failed' };
}

export type RedirectPaymentIntentQuery = UseQueryResult<RedirectIntentResponse, unknown> & {
  displayError: { status?: number | string; message: string } | null;
};

export function useRedirectPaymentIntent(token: string | null): RedirectPaymentIntentQuery {
  const query = useQuery({
    queryKey: ['paymentIntentRedirect', token],
    queryFn: async () => {
      const res = await getPaymentIntentRedirect(token!);
      if (!res.data?.widget_data) {
        throw new Error('missing_widget_data');
      }
      return res;
    },
    enabled: Boolean(token),
    staleTime: Number.POSITIVE_INFINITY
  });

  const displayError = query.isError ? parseRedirectIntentError(query.error) : null;

  return { ...query, displayError } as RedirectPaymentIntentQuery;
}
